/**
 * Normaliza enlaces introducidos por el usuario (añade https:// si falta el esquema).
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function normalizeOutboundUrl(raw) {
  const t = String(raw ?? '').trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (/^\/\//.test(t)) return `https:${t}`;
  if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(\/|$)/i.test(t)) return `https://${t}`;
  return t;
}

/** Plataformas disponibles en editar perfil (sin Discogs). */
export const SOCIAL_PLATFORMS = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'x', label: 'X (Twitter)' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
];

const ALLOWED_IDS = new Set(SOCIAL_PLATFORMS.map(p => p.id));

export function labelForSocialPlatform(id) {
  return SOCIAL_PLATFORMS.find(p => p.id === id)?.label ?? String(id);
}

function guessPlatformFromUrl(raw) {
  const t = String(raw ?? '').trim();
  if (!t) return null;
  try {
    const u = new URL(t.startsWith('http') ? t : `https://${t}`);
    const h = u.hostname.toLowerCase();
    if (h.includes('instagram')) return 'instagram';
    if (h.includes('facebook') || h === 'fb.com' || h.endsWith('.facebook.com')) return 'facebook';
    if (h.includes('twitter.com') || h === 'x.com' || h.endsWith('.x.com')) return 'x';
    if (h.includes('youtube.com') || h.includes('youtu.be')) return 'youtube';
    if (h.includes('tiktok.com')) return 'tiktok';
  } catch {
    return null;
  }
  return null;
}

/**
 * Filas { platform, url } para el formulario (máx. 3). Lee `social_links` o campos legacy (sin discogs).
 * @param {object|null|undefined} p
 * @returns {Array<{ platform: string, url: string }>}
 */
export function socialLinksFromProfile(p) {
  if (!p) return [];
  const raw = p.social_links;
  if (Array.isArray(raw) && raw.length > 0) {
    const out = [];
    for (const e of raw) {
      if (!e || typeof e !== 'object') continue;
      const pl = String(e.platform || '').toLowerCase();
      const url = String(e.url || '').trim();
      if (!ALLOWED_IDS.has(pl) || !url) continue;
      out.push({ platform: pl, url });
      if (out.length >= 3) break;
    }
    if (out.length > 0) return out;
  }

  const leg = [];
  const push = (platform, url) => {
    if (!ALLOWED_IDS.has(platform) || !String(url || '').trim()) return;
    if (leg.some(x => x.platform === platform)) return;
    leg.push({ platform, url: String(url).trim() });
  };

  push('instagram', p.instagram_url);
  push('x', p.twitter_url);
  const other = p.other_social_url?.trim();
  if (other) {
    const g = guessPlatformFromUrl(other);
    if (g) push(g, other);
  }
  return leg.slice(0, 3);
}

/**
 * Valida y normaliza filas del formulario para guardar en `profiles.social_links`.
 * @param {Array<{ platform?: string, url?: string }>} rows
 * @returns {{ ok: true, value: Array<{ platform: string, url: string }> } | { ok: false, message: string }}
 */
export function validateAndNormalizeSocialLinks(rows) {
  const cleaned = [];
  const seen = new Set();
  for (const r of rows || []) {
    const pl = String(r?.platform || '').toLowerCase();
    const urlNorm = normalizeOutboundUrl(r?.url);
    if (!pl && !urlNorm) continue;
    if (!ALLOWED_IDS.has(pl)) {
      return { ok: false, message: 'Elige una red válida en el desplegable.' };
    }
    // UX: si el usuario eligió plataforma pero dejó la URL vacía, no bloqueamos el guardado.
    // Simplemente ignoramos esa fila.
    if (!urlNorm) continue;
    if (seen.has(pl)) {
      return { ok: false, message: `No repitas la misma red (${labelForSocialPlatform(pl)}).` };
    }
    seen.add(pl);
    cleaned.push({ platform: pl, url: urlNorm });
    if (cleaned.length > 3) {
      return { ok: false, message: 'Como máximo 3 redes sociales.' };
    }
  }
  return { ok: true, value: cleaned };
}

/** Tres flags de visibilidad alineados con `social_links[0..2]` (orden de registro). */
export function publicSocialVisibilitySlots(p) {
  const raw = p?.public_social_visible;
  if (Array.isArray(raw)) {
    return [0, 1, 2].map(i => raw[i] !== false);
  }
  return [true, true, true];
}

/** Garantiza array de 3 booleanos para guardar en `profiles.public_social_visible`. */
export function normalizedSocialVisibilitySlots(slots) {
  const a = Array.isArray(slots) ? slots : [];
  return [0, 1, 2].map(i => a[i] !== false);
}

/**
 * Pares [etiqueta, url] para mostrar en perfil (web + redes).
 * Respeta `public_social_visible[i]` por cada entrada de `social_links` en orden.
 * @param {object|null|undefined} p
 * @returns {Array<[string, string]>}
 */
export function collectProfileSocialEntries(p) {
  if (!p) return [];
  const vis = publicSocialVisibilitySlots(p);
  const items = [];
  const w = String(p.website_url ?? '').trim();
  if (w) items.push(['Web', w]);
  const links = socialLinksFromProfile(p);
  links.forEach(({ platform, url }, i) => {
    if (vis[i] === false) return;
    items.push([labelForSocialPlatform(platform), url]);
  });
  return items;
}
